/**
 * HR Employees directory table — the `.card` + `.tbl` vocabulary shared with
 * the My-Leaves history table (`LeaveHistoryTable`), so the two surfaces read
 * as one design. Sortable headers (click to toggle asc/desc), horizontal
 * scroll on narrow viewports instead of clipping, and a per-row `⋮` overflow
 * menu for the Edit / Holidays / Flags / Reset actions.
 *
 * Pure display: it never fetches the directory (the page owns pagination via
 * usePagedList). It DOES own the small per-employee active-flag count
 * query so the "Flags" menu item can show the unresolved count inline.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { Employee, Team } from '@lms/shared-types'
import { roleSchema } from '@lms/shared-types'
import { Avatar } from '../primitives/Avatar'
import { RhythmAvatar } from '../manager-team/RhythmAvatar'
import { Badge, type BadgeVariant } from '../primitives/Badge'
import { Icon } from '../primitives/Icon'
import { Skeleton } from '../primitives/Skeleton'
import {
  RowActionsMenu,
  type RowAction,
} from '../primitives/RowActionsMenu'
import { roleLabel } from '../../lib/role'
import { fmtDate } from '../../lib/format'
import { flags } from '../../lib/feature-flags'
import { listEmployeeFlags } from '../../api/employee-flags'
import { getOrgChart } from '../../api/hr-org-chart'
import type { PasswordResetMode } from './PasswordResetModal'

// ── Employment type ───────────────────────────────────────────────────────────

// Human-readable label for the `employment_type` enum. Copy matches the HR edit
// form and the employee's own MePage so the three surfaces read the same.
function employmentTypeLabel(type: Employee['employmentType']): string {
  return type === 'contractual' ? 'Contractual' : 'Full-time'
}

// Distinct badge colours so employment type is scannable and the temporary
// (contractual) case stands out. Kept off `info`, which the Role badge uses.
function employmentTypeBadge(type: Employee['employmentType']): BadgeVariant {
  return type === 'contractual' ? 'warning' : 'pear'
}

// ── Sort ─────────────────────────────────────────────────────────────────────

type SortColumn = 'name' | 'approver' | 'team' | 'role' | 'joined' | 'status'
type SortDir = 'asc' | 'desc'
interface SortState {
  col: SortColumn
  dir: SortDir
}

interface HeaderCol {
  id: SortColumn | 'actions'
  label: string
  sortable: boolean
  align?: 'left' | 'right'
  // Proportional column width for the fixed table layout. The six widths sum
  // to 100% so the table fills its container without horizontal scroll; text
  // columns truncate (ellipsis) rather than pushing the table wider.
  width: string
}

// Column set depends on whether the "Joined" column is shown (gated by the
// `employeeDirectoryEnhancements` flag via the `showJoinDate` prop). Widths in
// each variant sum to 100% so the fixed-layout table fills its container in one
// view — adding Joined rebalances the others rather than overflowing.
function buildHeaders(showJoinDate: boolean): ReadonlyArray<HeaderCol> {
  if (showJoinDate) {
    return [
      { id: 'name', label: 'Name', sortable: true, width: '22%' },
      { id: 'approver', label: 'Approver', sortable: true, width: '16%' },
      { id: 'team', label: 'Team', sortable: true, width: '17%' },
      { id: 'role', label: 'Role', sortable: true, width: '15%' },
      { id: 'joined', label: 'Joined', sortable: true, width: '13%' },
      { id: 'status', label: 'Status', sortable: true, width: '9%' },
      { id: 'actions', label: 'Actions', sortable: false, align: 'right', width: '8%' },
    ]
  }
  return [
    { id: 'name', label: 'Name', sortable: true, width: '25%' },
    { id: 'approver', label: 'Approver', sortable: true, width: '18%' },
    { id: 'team', label: 'Team', sortable: true, width: '20%' },
    { id: 'role', label: 'Role', sortable: true, width: '18%' },
    { id: 'status', label: 'Status', sortable: true, width: '11%' },
    { id: 'actions', label: 'Actions', sortable: false, align: 'right', width: '8%' },
  ]
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface EmployeesTableProps {
  employees: ReadonlyArray<Employee>
  teams: ReadonlyArray<Team>
  loading: boolean
  error?: string | undefined
  onRetry: () => void
  emptyState: ReactNode
  onEdit: (emp: Employee) => void
  onPassword: (target: { employee: Employee; mode: PasswordResetMode }) => void
  /** Rendered inside the card beneath the table (e.g. PaginationControls). */
  footer?: ReactNode
  /** Show the "Joined" (join-date) column. Gated by the directory-enhancements
   *  flag at the page; defaults off so the stock table is unchanged. */
  showJoinDate?: boolean
}

export function EmployeesTable({
  employees,
  teams,
  loading,
  error,
  onRetry,
  emptyState,
  onEdit,
  onPassword,
  footer,
  showJoinDate = false,
}: EmployeesTableProps): JSX.Element {
  const [sort, setSort] = useState<SortState>({ col: 'name', dir: 'asc' })
  const headers = useMemo(() => buildHeaders(showJoinDate), [showJoinDate])

  const teamNameById = useMemo(
    () => new Map(teams.map((t) => [t.id, t.name])),
    [teams],
  )

  // Resolve each employee's primary approver id → name. The approver may live
  // on another page, so names come from the org chart (all employees), not the
  // current page. HR-only surface, same access as this table.
  const orgQuery = useQuery({
    queryKey: ['org-chart'],
    queryFn: ({ signal }) => getOrgChart(signal),
  })
  const nameById = useMemo(
    () => new Map((orgQuery.data?.employees ?? []).map((e) => [e.id, e.name])),
    [orgQuery.data],
  )
  const approverNameOf = (emp: Employee): string =>
    emp.primaryApproverId ? nameById.get(emp.primaryApproverId) ?? '—' : '—'

  const sortValue = (emp: Employee): string => {
    switch (sort.col) {
      case 'approver':
        return approverNameOf(emp)
      case 'team':
        return teamNameById.get(emp.teamId ?? '') ?? ''
      case 'role':
        return emp.role
      case 'joined':
        // ISO join date sorts lexically = chronologically.
        return emp.joinDate ?? ''
      case 'status':
        return emp.active ? 'active' : 'inactive'
      case 'name':
      default:
        return emp.name
    }
  }

  const sortedRows = useMemo(() => {
    const rows = [...employees]
    rows.sort((a, b) => {
      const av = sortValue(a).toLowerCase()
      const bv = sortValue(b).toLowerCase()
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return sort.dir === 'asc' ? cmp : -cmp
    })
    return rows
    // sortValue closes over sort + teamNameById + nameById
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, sort, teamNameById, nameById])

  function toggleSort(col: SortColumn): void {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' },
    )
  }

  return (
    // Fixed layout with proportional column widths so the directory fits the
    // container width in one view — no horizontal scroll. Text columns truncate
    // (ellipsis) instead of widening the table. overflowX stays as a safety net
    // for extremely narrow (mobile) viewports where the fixed columns would
    // otherwise cramp below legibility.
    <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table
        className="tbl"
        data-testid="employees-tbl"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <thead>
          <tr>
            {headers.map((h) => {
              const active = h.sortable && sort.col === h.id
              return (
                <th
                  key={h.id}
                  scope="col"
                  aria-sort={
                    active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  style={{
                    width: h.width,
                    textAlign: h.align ?? 'left',
                    cursor: h.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={h.sortable ? () => toggleSort(h.id as SortColumn) : undefined}
                  tabIndex={h.sortable ? 0 : undefined}
                  onKeyDown={
                    h.sortable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            toggleSort(h.id as SortColumn)
                          }
                        }
                      : undefined
                  }
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      justifyContent: h.align === 'right' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {h.id === 'actions' ? (
                      <span aria-label="Actions">{h.label}</span>
                    ) : (
                      h.label
                    )}
                    {active ? (
                      <Icon
                        name={sort.dir === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down'}
                        size={16}
                        aria-hidden
                        style={{ color: 'var(--steel-blue)' }}
                      />
                    ) : null}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows headers={headers} />
          ) : error ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: '20px 14px' }}>
                <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{error} </span>
                <button
                  type="button"
                  onClick={onRetry}
                  data-testid="employees-retry"
                  style={{
                    background: 'none',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '2px 10px',
                    fontSize: 12,
                    color: 'var(--text-2)',
                    cursor: 'pointer',
                  }}
                >
                  Retry
                </button>
              </td>
            </tr>
          ) : sortedRows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} style={{ padding: 0 }}>
                {emptyState}
              </td>
            </tr>
          ) : (
            sortedRows.map((emp) => (
              <EmployeeRow
                key={emp.id}
                employee={emp}
                teamName={teamNameById.get(emp.teamId ?? '') ?? '—'}
                approverName={approverNameOf(emp)}
                showJoinDate={showJoinDate}
                onEdit={onEdit}
                onPassword={onPassword}
              />
            ))
          )}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

// ── Row ──────────────────────────────────────────────────────────────────────

interface EmployeeRowProps {
  employee: Employee
  teamName: string
  approverName: string
  showJoinDate: boolean
  onEdit: (emp: Employee) => void
  onPassword: (target: { employee: Employee; mode: PasswordResetMode }) => void
}

const ellipsis: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  // Fill the (fixed) column width and truncate there, rather than a fixed cap.
  maxWidth: '100%',
}

function EmployeeRow({
  employee,
  teamName,
  approverName,
  showJoinDate,
  onEdit,
  onPassword,
}: EmployeeRowProps): JSX.Element {
  // Show every role the employee holds. The list response already carries the
  // full set (`roles`, sorted by rank server-side); fall back to the single
  // primary `role` for older responses that predate the field.
  const roleValues =
    employee.roles && employee.roles.length > 0 ? employee.roles : [employee.role]
  const roleBadges = roleValues.map((value) => {
    const parsed = roleSchema.safeParse(value)
    return { value, text: parsed.success ? roleLabel(parsed.data) : value }
  })

  return (
    // The whole row opens the edit form (the ⋮ menu's Edit item stays too). The
    // row is keyboard-operable (Enter / Space) and shows a pointer + hover
    // highlight (.tbl tr:hover). Mirrors the Table primitive's onRowClick.
    <tr
      onClick={() => onEdit(employee)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onEdit(employee)
        }
      }}
      tabIndex={0}
      aria-label={`Edit ${employee.name}`}
      style={{ cursor: 'pointer' }}
    >
      <td>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {employee.health ? (
            // Leave-rhythm indicator (band colour + glyph), matching the
            // manager Team roster. Falls back to initials when the list
            // response predates the health field.
            <RhythmAvatar
              name={employee.name}
              band={employee.health.band}
              patternCount={employee.health.contributingPatterns.length}
              size={32}
            />
          ) : (
            <Avatar name={employee.name} />
          )}
          <span style={{ minWidth: 0 }}>
            <span
              title={employee.name}
              style={{ fontWeight: 600, color: 'var(--text)', ...ellipsis }}
            >
              {employee.name}
            </span>
            <span
              title={employee.email}
              style={{ fontSize: 12, color: 'var(--text-3)', ...ellipsis }}
            >
              {employee.email}
            </span>
            <span
              style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}
            >
              <Badge variant={employmentTypeBadge(employee.employmentType)}>
                {employmentTypeLabel(employee.employmentType)}
              </Badge>
              {/* Probation status chip — part of the directory-enhancements
                  slice, so it reverts with the same kill-switch. */}
              {flags.employeeDirectoryEnhancements && employee.onProbation ? (
                <Badge variant="blue">Probation</Badge>
              ) : null}
            </span>
          </span>
        </span>
      </td>
      <td style={{ color: 'var(--text-2)' }}>
        <span title={approverName} style={ellipsis}>
          {approverName}
        </span>
      </td>
      <td style={{ color: 'var(--text-2)' }}>
        <span title={teamName} style={ellipsis}>
          {teamName}
        </span>
      </td>
      <td>
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {roleBadges.map((badge) => (
            <Badge key={badge.value} variant="info">
              {badge.text}
            </Badge>
          ))}
        </span>
      </td>
      {showJoinDate ? (
        <td style={{ color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
          <span title={employee.joinDate ?? undefined} style={ellipsis}>
            {employee.joinDate ? fmtDate(employee.joinDate) : '—'}
          </span>
        </td>
      ) : null}
      <td>
        {employee.active ? (
          <Badge variant="success" dot>
            Active
          </Badge>
        ) : (
          <Badge variant="default" dot>
            Inactive
          </Badge>
        )}
      </td>
      <td
        style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
        // Clicks in the actions cell drive the ⋮ menu, not the row-edit.
        onClick={(e) => e.stopPropagation()}
      >
        <EmployeeRowActions
          employee={employee}
          onEdit={onEdit}
          onPassword={onPassword}
        />
      </td>
    </tr>
  )
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function SkeletonRows({
  headers,
}: {
  headers: ReadonlyArray<HeaderCol>
}): JSX.Element {
  return (
    <>
      {Array.from({ length: 5 }, (_, i) => (
        <tr key={i} aria-hidden>
          {headers.map((h) => (
            <td key={h.id}>
              <Skeleton height={14} width={h.id === 'name' ? '55%' : '75%'} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

// ── Row actions (⋮) ────────────────────────────────────────────────────────

interface EmployeeRowActionsProps {
  employee: Employee
  onEdit: (emp: Employee) => void
  onPassword: (target: { employee: Employee; mode: PasswordResetMode }) => void
}

function EmployeeRowActions({
  employee,
  onEdit,
  onPassword,
}: EmployeeRowActionsProps): JSX.Element {
  const navigate = useNavigate()
  const flagsQuery = useQuery({
    queryKey: ['employee-flags', employee.id, false],
    queryFn: () => listEmployeeFlags(employee.id, false),
    staleTime: 30_000,
    enabled: flags.hrEmployeeFlags,
  })
  const flagCount = flagsQuery.data?.length ?? 0

  const actions: RowAction[] = [
    {
      icon: 'edit',
      label: 'Edit',
      testId: 'edit-employee',
      onClick: () => onEdit(employee),
    },
  ]

  if (flags.hrEmployeeFlags) {
    actions.push({
      icon: 'flag',
      label: flagCount > 0 ? `Flags · ${flagCount}` : 'Flags',
      testId: 'flags-employee',
      onClick: () =>
        void navigate({
          to: '/hr/employees/$employeeId/flags',
          params: { employeeId: employee.id },
        }),
    })
  }

  if (flags.passwordReset) {
    actions.push(
      {
        icon: 'lock_reset',
        label: 'Reset password',
        testId: 'reset-password-employee',
        hint: 'Issues a temp password and forces rotation at next login',
        onClick: () => onPassword({ employee, mode: 'reset' }),
      },
      {
        icon: 'password',
        label: 'Require change',
        testId: 'force-change-password-employee',
        hint: 'Requires a password change at next login',
        onClick: () => onPassword({ employee, mode: 'force-change' }),
      },
    )
  }

  return (
    <RowActionsMenu
      ariaLabel={`Actions for ${employee.name}`}
      testId={`employee-actions-${employee.id}`}
      actions={actions}
    />
  )
}
